import { useCallback, useState } from 'react';
import { isException, isSuccess } from 'resultful';

import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import {
	type EntitlementDetails,
	type OrderDefault,
	type OrderRequestDto,
	// eslint-disable-next-line camelcase
	orders_post_v1,
} from '@atlassian/commerce-ccp-gateway-client';

import { waitUntilOrderSucceeds } from '@atlassian/commerce-ui/entitlements';

import { useCommerceFetch } from '@atlassian/commerce-environment';

import {
	FREE_EDITION,
	PREMIUM_EDITION,
	STANDARD_EDITION,
	UNLICENSED,
	type ApplicationEdition,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { createAmendmentOrderRequestDto } from '../common/createAmendmentOrderRequestDto.tsx';
import { recordTosAndPrivacyConsent } from '../services/recordTosAndPrivacyConsent.tsx';

export const NO_USAGE_REPORTED_ERROR_CODE = 'E2102';
export const ORDER_RETRY_COUNT = 3;

export type EntitlementUpgradeQueryProps = {
	shouldUpgrade: boolean;
	entitlementDetails: EntitlementDetails | undefined | null;
	defaultOffering: OrderDefault | null;
	targetOfferingId: string | null;
	retryMs?: number;
	targetEdition: ApplicationEdition | null;
	transactionAccountId: string | null;
	analyticsAttributes?: Record<string, unknown>;
};

const ordersPostWithRetry = async (
	payload: {
		body: OrderRequestDto;
		headers: {
			'X-transaction-account': string;
		};
	},
	retryIntervalMs = 100,
	maxRetries = 0,
	// eslint-disable-next-line camelcase
): Promise<ReturnType<typeof orders_post_v1> & Promise<{ retry: number }>> => {
	let retry = 0;
	let result;

	do {
		// eslint-disable-next-line no-await-in-loop
		result = await orders_post_v1(payload);
		if (result.payload?.status === 201 || isException(result)) {
			return { ...result, retry };
		}
		const usageError = result.payload?.body?.code === NO_USAGE_REPORTED_ERROR_CODE;

		if (!usageError) {
			return { ...result, retry };
		}

		// sleep for interval
		// eslint-disable-next-line no-await-in-loop
		await new Promise((resolve) => setTimeout(resolve, retryIntervalMs));
		retry += 1;
	} while (retry < maxRetries);
	return { ...result, retry };
};

// Upgrades product edition after capturing consent
export const useUpgradeEdition = ({
	shouldUpgrade,
	entitlementDetails,
	defaultOffering,
	targetOfferingId,
	targetEdition,
	transactionAccountId,
	retryMs = 22000,
	analyticsAttributes,
}: EntitlementUpgradeQueryProps) => {
	const fetch = useCommerceFetch();
	const [entitlementUpgrading, setEntitlementUpgrading] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireErrorAnalytics = useCallback(
		(reason: string, attributes?: Record<string, unknown>) =>
			fireOperationalAnalytics(createAnalyticsEvent({}), 'useUpgradeEdition failed', {
				...attributes,
				...analyticsAttributes,
				reason,
				shouldUpgrade,
				targetEdition,
				targetOfferingId,
			}),
		[analyticsAttributes, createAnalyticsEvent, shouldUpgrade, targetEdition, targetOfferingId],
	);

	const fireSuccessAnalytics = useCallback(
		(actionSubject: string, attributes?: Record<string, unknown>) =>
			fireOperationalAnalytics(createAnalyticsEvent({}), `${actionSubject} succeeded`, {
				...attributes,
				...analyticsAttributes,
				shouldUpgrade,
				targetEdition,
				targetOfferingId,
			}),
		[analyticsAttributes, createAnalyticsEvent, shouldUpgrade, targetEdition, targetOfferingId],
	);

	const fireStepSuccessAnalytics = useCallback(
		(step: string, attributes?: Record<string, unknown>) =>
			fireOperationalAnalytics(createAnalyticsEvent({}), 'useUpgradeEdition stepSucceeded', {
				...attributes,
				...analyticsAttributes,
				shouldUpgrade,
				targetEdition,
				targetOfferingId,
			}),
		[analyticsAttributes, createAnalyticsEvent, shouldUpgrade, targetEdition, targetOfferingId],
	);

	const upgradeCCPEntitlement = useCallback(
		async ({ tosPrivacyText, locale }: { tosPrivacyText: string; locale: string }) => {
			if (!shouldUpgrade) {
				fireErrorAnalytics('upgradeSkipped');
				return;
			}
			if (
				entitlementUpgrading ||
				!entitlementDetails ||
				!targetOfferingId ||
				!defaultOffering ||
				!transactionAccountId ||
				!tosPrivacyText
			) {
				fireErrorAnalytics('upgradeCCPEntitlementCalledWithInvalidState');
				return;
			}

			try {
				setEntitlementUpgrading(true);
				if (tosPrivacyText) {
					// Only record consent if payment details are available
					// Convert locale to 2-character country code (ISO 3166-1 alpha-2)
					const countryCode = locale.split('-').pop()?.toUpperCase() || 'US';
					await recordTosAndPrivacyConsent({
						tosPrivacyText,
						geoLocale: countryCode,
					});
					fireStepSuccessAnalytics('consentRecorded');
				}

				const orderRequest = createAmendmentOrderRequestDto(
					defaultOffering,
					targetOfferingId,
					entitlementDetails,
				);

				if (!orderRequest) {
					fireErrorAnalytics('orderRequestNotCreated');
					return;
				}

				const result = await ordersPostWithRetry(
					{
						body: orderRequest,
						headers: {
							'X-transaction-account': transactionAccountId,
						},
					},
					retryMs,
					ORDER_RETRY_COUNT,
				);

				if (isSuccess(result) && result.payload.status === 201) {
					await waitUntilOrderSucceeds(fetch, transactionAccountId, orderRequest.orderId);
					fireSuccessAnalytics('useUpgradeEdition', result);
				} else {
					if (result.retry === ORDER_RETRY_COUNT) {
						// Could not upgrade entitlement after max retries
						fireErrorAnalytics('upgradeFailedAfterMaxRetries', {
							entitlementUpgradeError: JSON.stringify(result),
							entitlementUpgradeErrorCode: result.error?.status,
						});
						setEntitlementUpgrading(false);
						return;
					}

					// encountered an error before max retries
					fireErrorAnalytics('upgradeFailedBeforeMaxRetries', {
						entitlementUpgradeErrorCode: result.error?.status,
						entitlementUpgradeError: JSON.stringify(result),
					});
				}
			} catch (error) {
				fireErrorAnalytics('upgradeFailedDueToException', {
					entitlementUpgradeError: JSON.stringify(error),
				});
			} finally {
				setEntitlementUpgrading(false);
			}
		},
		[
			shouldUpgrade,
			entitlementUpgrading,
			entitlementDetails,
			targetOfferingId,
			defaultOffering,
			transactionAccountId,
			fireErrorAnalytics,
			retryMs,
			fireStepSuccessAnalytics,
			fetch,
			fireSuccessAnalytics,
		],
	);

	return { upgradeCCPEntitlement, entitlementUpgrading };
};

export const targetEditionToUpgradeOfferingId = ({
	productKey,
	targetEdition,
}: {
	productKey: string;
	targetEdition: ApplicationEdition;
}) => {
	const offeringIdMap: Record<string, Record<ApplicationEdition, string | null>> = {
		'jira-product-discovery': {
			[STANDARD_EDITION]: '35b9268e-66e2-45fb-81d3-dacd1e31831a',
			[PREMIUM_EDITION]: 'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd',
			[UNLICENSED]: null,
			[FREE_EDITION]: null,
		},
	};
	if (!offeringIdMap[productKey]) {
		throw new Error(`Not supported product key: ${productKey}`);
	}
	if (!offeringIdMap[productKey][targetEdition]) {
		throw new Error(
			`Not supported target edition: ${targetEdition} for product key: ${productKey}`,
		);
	}
	const offeringId = offeringIdMap[productKey][targetEdition];
	if (!offeringId) {
		throw new Error(
			`Upgrade Offering ID not found for product key: ${productKey} and edition: ${targetEdition}`,
		);
	}
	return offeringId;
};
